import { type ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * PlotDotLegend — a colored-DOT legend (round swatches + labels) shared by the DOM bar
 * families. Built from the legacy `DivergingLegend` impl by swapping its `rounded-sm`
 * squares for `rounded-full` dots.
 *
 * DELIBERATE DEVIATION: our `dataviz` marks spec says a legend swatch should MIRROR the
 * bar mark (a rect at the baseline). Dots are a reference-driven choice we are keeping —
 * so this is intentionally NOT a dataviz-conformance component; it is a reference-clean
 * deviation. Do not "fix" the dots back into squares to match the marks spec.
 *
 * Layout/text color are intentionally caller-owned via `className` so a surface can pass
 * its own muted-foreground token; the primitive only fixes the dot + row geometry.
 */
export interface PlotDotLegendItem {
  /** Dot color — an `hsl(var(--token))` string (never a raw hex). */
  color: string
  label: ReactNode
}

export interface PlotDotLegendProps {
  items: Array<PlotDotLegendItem>
  /** Container classes (layout + text color). Default a wrapped flex row. */
  className?: string
  'data-testid'?: string
}

export function PlotDotLegend(props: PlotDotLegendProps) {
  const { items, className, 'data-testid': dataTestId } = props

  return (
    <div
      className={cn('flex flex-wrap items-center gap-x-4 gap-y-1', className)}
      data-testid={dataTestId}
    >
      {items.map((item, i) => (
        <span key={i} className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: item.color }} />
          {item.label}
        </span>
      ))}
    </div>
  )
}
